/*
 * Originally extracted from intake-modal.tsx (authored by Tony Ramirez).
 * The "elevated onboarding" shell — non-dismissible blurred-backdrop modal
 * with a 62/38 column split (content left, hero panel right) and a thin
 * product-header strip. State primitives (onboarding provider, step
 * transitions, etc.) live in consumer code; this component is chrome only.
 */
import { useTranslation } from "@redotech/redo-i18n";
import RedoLogo from "@redotech/redo-icons/redo-logo-cutout.svg";
import classNames from "classnames";
import { ComponentType, memo, ReactNode } from "react";
import { RedoModal } from "../arbiter-components/modal/redo-modal";
import { RedoProgressBar } from "../arbiter-components/progress-bar/redo-progress-bar";
import {
  ButtonSize,
  RedoButtonCloseX,
} from "../arbiter-components/redo-button-close-x";
import { Flex } from "../flex";
import * as css from "./redo-elevated-onboarding.module.css";

interface RedoElevatedOnboardingProps {
  /**
   * Modal title (passed to the underlying `RedoModal` for a11y). Also rendered
   * as a visible label next to the product icon in the header strip when
   * `hideProductName` is not set.
   */
  productName: string;
  /**
   * Hide the visible product-name label in the header strip while still using
   * `productName` as the accessible modal title. Useful when the consumer wants
   * just the logo (e.g. first-install onboarding).
   */
  hideProductName?: boolean;
  /** Icon shown at the top-left of the content column. Defaults to the Redo logo cutout. */
  ProductIcon?: ComponentType<{ className?: string }>;
  /** When provided, renders a thin progress bar in the header strip. */
  progressPercent?: number;
  /** Render a prominent exit button in the modal's top-right corner. */
  showExitButton?: boolean;
  /** Right column — typically a `<RedoElevatedOnboardingHeroPanel>`. */
  rightPanel: ReactNode;
  /** Optional band rendered below the body — does not scroll with the body. */
  footer?: ReactNode;
  /**
   * When true, the chrome's footer wrapper renders flush (no padding) so the
   * supplied `footer` node controls its own inset. Use this when passing a
   * footer component that already manages padding (e.g. `RedoOnboardingFooter`)
   * to avoid double-padding the footer band.
   */
  footerFlush?: boolean;
  /**
   * Step body. Scrolls inside the content column by default; pass
   * `bodyScroll={false}` if the consumer wants to manage overflow itself.
   */
  children?: ReactNode;
  /**
   * When false (default), the consumer's body sits inside a scrollable flex
   * column. When true, overflow is left to the consumer.
   */
  bodyScroll?: boolean;
  /** Non-dismissible by default. When true, exposes a close button + escape/backdrop close. */
  dismissible?: boolean;
  /** Invoked only when `dismissible` is true. */
  onCloseRequested?: () => void;
  /** Optional className applied to the body wrapper. */
  bodyClassName?: string;
}

export const RedoElevatedOnboarding = memo(function RedoElevatedOnboarding({
  productName,
  hideProductName = false,
  ProductIcon = RedoLogo,
  progressPercent,
  showExitButton = false,
  rightPanel,
  footer,
  footerFlush = false,
  children,
  bodyScroll = true,
  dismissible = false,
  onCloseRequested,
  bodyClassName,
}: RedoElevatedOnboardingProps) {
  const { t } = useTranslation();
  return (
    <RedoModal
      className={css.modal}
      contentScrollable
      hideCloseButton={!dismissible}
      hideFooter
      hideHeader
      isOpen
      mainContentClassName={css.modalMain}
      modalSize="xl"
      muiClassName={css.backdropBlur}
      noPadding
      onModalCloseRequested={
        dismissible ? () => onCloseRequested?.() : swallowClose
      }
      title={productName}
    >
      <Flex
        className={css.frame}
        data-arbiter="RedoElevatedOnboarding"
        dir="row"
        position="relative"
      >
        <div className={css.contentColumn}>
          <div className={css.productHeader}>
            <ProductIcon className={css.productHeaderIcon} />
            {!hideProductName && (
              <span className={css.productHeaderName}>{productName}</span>
            )}
            {progressPercent != null && (
              <div
                aria-label={t("Onboarding progress", { context: "onboarding" })}
                aria-valuemax={100}
                aria-valuemin={0}
                aria-valuenow={progressPercent}
                className={css.progressIndicator}
                role="progressbar"
              >
                <RedoProgressBar
                  labelStyle="none"
                  percentage={progressPercent}
                  style="thin"
                />
              </div>
            )}
          </div>

          <div
            className={classNames(
              css.body,
              bodyScroll && css.bodyScroll,
              bodyClassName,
            )}
          >
            {children}
          </div>

          {footer && (
            <div
              className={classNames(
                css.columnFooter,
                footerFlush && css.columnFooterFlush,
              )}
            >
              {footer}
            </div>
          )}
        </div>

        <div className={css.imageWrapper}>{rightPanel}</div>

        {showExitButton && onCloseRequested && (
          <Flex
            bgColor="secondary-solid"
            borderRadius="full"
            position="absolute"
            right="3xl"
            shadow="lg"
            top="3xl"
          >
            <RedoButtonCloseX
              onclick={onCloseRequested}
              onDarkBackground
              size={ButtonSize.MEDIUM}
            />
          </Flex>
        )}
      </Flex>
    </RedoModal>
  );
});

function swallowClose(): void {
  // Intentional no-op — non-dismissible.
}
